import React, { useEffect, useState, useRef } from 'react';
import {
  DatePicker,
  Select,
  Button,
  Form,
  Cascader,
  Input,
  Upload,
  message,
  Radio,
  Checkbox,
  Image,
} from 'antd';
import moment from 'moment';
import 'moment/locale/zh-cn';
import locale from 'antd/es/date-picker/locale/zh_CN';
import './index.scss';
import { cloneDeep } from 'lodash';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { formatCommonCategory, getBase64 } from '@/utils/util';
import dayjs from 'dayjs';
import { PlusOutlined } from '@ant-design/icons';
import RemoteUploader from './RemoteUploader';
import useHandleRemoteUpload from './RemoteUploader/utils/useHandleRemoteUpload';

const { RangePicker } = DatePicker;
export interface FormProps {
  config: FormConfig;
  enableSelectAllField?: string;
  onSearch?: AnyFunc;
  onReset?: AnyFunc;
  onChange?: AnyFunc;
  onFieldChange?: AnyFunc;
  onBlur?: AnyFunc;
  onValuesChange?: AnyFunc;
  layout?: 'horizontal' | 'vertical' | 'inline';
  defaultValue?: {
    [key in string]: any;
  };
  getFormInstance?: (formRef: any) => any;
  className?: string;
  labelAlign?: 'left' | 'right' | undefined;
}

const CommonForm = (props: FormProps) => {
  const {
    config,
    onSearch,
    onReset,
    onChange,
    onFieldChange,
    layout = 'inline',
    defaultValue,
    getFormInstance,
    className,
    onValuesChange,
    labelAlign,
    enableSelectAllField,
  } = props;
  const customValidatorRef = useRef<any>([]);
  const [form] = Form.useForm();
  const [formData, setFormData] = useState<any>(null);
  const [allSelected, setAllSelected] = useState(true);
  const [occurTime, setOccurTime] = useState<any[]>([
    dayjs().format('YYYY-MM-DD HH:mm:ss'),
  ]);
  const [upLoadState, setUpLoadState] = useState<any>({
    previewVisible: false,
    previewImage: '',
    previewTitle: '',
    fileList: [],
  });
  const remoteUploadProps = useHandleRemoteUpload({ config, onFieldChange }); // 处理文件上传至s3的逻辑hook

  useEffect(() => {
    config?.data?.forEach((item) => {
      config?.linkRules?.forEach((rules) => {
        if (item.fieldName === rules.linkFieldName) {
          customValidatorRef.current.push({
            fieldName: item.fieldName,
            validatorRules: item.validatorRules,
          });
        }
      });
    });
  }, []);

  useEffect(() => {
    const formatData = formatFormData(cloneDeep(config));
    setFormData(formatData);
  }, [JSON.stringify(config)]);
  // 限制当天以后的日期不可选
  const disabledDate = (current: any) => {
    return current && current >= moment().endOf('days');
  };
  const handlePreview = async (file: any) => {
    if (!file.preview) {
      file.preview = await getBase64(file.originFileObj);
    }
    const imgCurrentIndex = upLoadState.fileList.findIndex((item: any) => {
      return item.uid == file.uid;
    });
    const fileData = {
      ...upLoadState,
      previewImage: file.preview,
      previewVisible: true,
      previewTitle:
        file.name || file.url.substring(file.url.lastIndexOf('/') + 1),
      imgCurrentIndex: imgCurrentIndex,
    };
    setUpLoadState(fileData);
    form?.setFieldValue('attachment', fileData);
  };
  const onAllSelectChange = (e: CheckboxChangeEvent) => {
    if (enableSelectAllField) {
      setAllSelected(e.target.checked);
      const field = formData?.data?.find(
        (item: any) => item.fieldName === enableSelectAllField
      );
      if (e.target.checked) {
        form.setFieldsValue({
          [enableSelectAllField]: formatCommonCategory(field.data),
        });
      } else {
        form.setFieldsValue({
          [enableSelectAllField]: [],
        });
      }
    }
  };

  const changeTime = (value: any) => {
    if (value) {
      checkOccurTime(value);
    }
  };
  // 校验“发生时间” 不允许添加已经存在的过的时间
  const checkOccurTime = (value: any) => {
    const repeat: any[] = occurTime.filter(
      (item: any) => item === dayjs(value.$d).format('YYYY-MM-DD HH:mm:ss')
    );
    if (repeat.length > 0) {
      message.info('时间有重复，请重新选择');
    } else {
      const newTimeList: any = [
        ...occurTime,
        moment(value.$d).format('YYYY-MM-DD HH:mm:ss'),
      ];
      form.setFieldsValue({ debugTime: newTimeList });
      setOccurTime(newTimeList);
    }
  };

  // 图片上传
  const uploadChange = ({ file, fileList }: any) => {
    const newFileList: any[] = fileList.map((item: any) => {
      return file.uid == item.uid
        ? { ...item, uidName: `${item.uid}${item.name}` }
        : item;
    });
    const fileData = { ...upLoadState, fileList: newFileList };
    setUpLoadState(fileData);
    form?.setFieldValue('attachment', fileData);
  };

  /**
   * 上传附件
   * @param {any} option
   */
  const customRequest = (option: any) => {
    const reader = new FileReader();
    reader.readAsDataURL(option.file);
    reader.onloadend = function (e: any) {
      option.onSuccess();
    };
  };

  const renderFieldItem = (dataItem: FieldItem) => {
    const {
      fieldName,
      cmpType,
      data,
      placeholder,
      label,
      multiple,
      validatorRules = [],
      type = '.jpg, .jpeg, .png',
      max = 5,
      hasSearch,
      maxLength,
      allowClear,
      changeOnSelect,
      disabled,
      showSelectAll,
      showUploadList,
      actionUrl,
      customStyle,
    } = dataItem;
    switch (cmpType) {
      case 'input':
        return (
          <Input
            placeholder={placeholder ?? ''}
            type={type ?? 'text'}
            allowClear={typeof allowClear === 'undefined' ? true : allowClear}
          />
        );
      case 'radioGroup':
        return <Radio.Group options={data} />;
      case 'textarea':
        return (
          <Input.TextArea
            placeholder={placeholder ?? ''}
            autoSize={{ minRows: 2, maxRows: 6 }}
            onBlur={(e) => {
              onFieldChange && onFieldChange(fieldName, e.target.value);
            }}
            maxLength={maxLength}
            showCount={{
              formatter: ({ value, count, maxLength }) =>
                `${count}/${maxLength}`,
            }}
          />
        );
      case 'select':
        return (
          <Select
            placeholder={placeholder ?? ''}
            options={data ?? []}
            showSearch={!!hasSearch}
            allowClear={typeof allowClear === 'undefined' ? true : allowClear}
            filterOption={!!hasSearch}
            onChange={(val) => {
              onFieldChange && onFieldChange(fieldName, val);
            }}
          />
        );
      case 'rangeTime':
        return (
          <RangePicker
            locale={locale}
            showTime
            format="YYYY-MM-DD HH:mm:ss"
            disabledDate={disabledDate}
          />
        );
      case 'dateTime':
        return (
          <DatePicker
            allowClear={typeof allowClear === 'undefined' ? true : allowClear}
            locale={locale}
            showTime
            format="YYYY-MM-DD HH:mm:ss"
            disabledDate={disabledDate}
            onChange={(val) => {
              onFieldChange &&
                onFieldChange(fieldName, val?.format('YYYY-MM-DD HH:mm:ss'));
            }}
            disabled={disabled}
            style={customStyle}
          />
        );
      case 'cascader':
        return (
          <Cascader
            fieldNames={{ label: 'name', value: 'id', children: 'child' }}
            options={data}
            placeholder={placeholder ?? ''}
            changeOnSelect={!!changeOnSelect}
            size={'middle'}
            style={{ width: '100%' }}
            maxTagCount={2}
            onChange={onChange}
            showSearch={!!hasSearch}
            allowClear={!!allowClear}
            multiple={multiple}
            getPopupContainer={(triggerNode) => triggerNode.parentElement}
            showCheckedStrategy={Cascader.SHOW_CHILD}
            dropdownRender={(menus) => {
              return (
                <>
                  {showSelectAll && (
                    <Checkbox
                      className="select-all"
                      onChange={onAllSelectChange}
                      checked={allSelected}
                    >
                      全部
                    </Checkbox>
                  )}
                  {menus}
                </>
              );
            }}
          />
        );
      case 'custom':
        return (
          <div className="re-picker">
            <Select
              placeholder={placeholder}
              mode="multiple"
              value={occurTime}
              onChange={(value) => {
                form.setFieldsValue({ [fieldName]: value });
                setOccurTime(value);
              }}
              open={false}
            ></Select>
            <DatePicker
              showTime
              onChange={changeTime}
              onOk={checkOccurTime}
              inputReadOnly={true}
              className="defaultIcon"
              allowClear={false}
              needConfirm={false}
            />
          </div>
        );
      case 'upload':
        return (
          <div className="upload-box">
            <Upload
              listType="picture-card"
              fileList={upLoadState.fileList}
              onPreview={handlePreview}
              onChange={uploadChange}
              customRequest={customRequest}
            >
              {upLoadState.fileList.length >= 3 ? null : (
                <div>
                  {' '}
                  <PlusOutlined />
                </div>
              )}
            </Upload>
            {upLoadState.previewImage && upLoadState.previewImage && (
              <Image
                wrapperStyle={{ display: 'none' }}
                preview={{
                  visible: upLoadState.previewVisible,
                  onVisibleChange: (visible) => {
                    const fileData = {
                      ...upLoadState,
                      previewVisible: visible,
                      previewImage: '',
                    };
                    setUpLoadState(fileData);
                    form?.setFieldValue('attachment', fileData);
                  },
                }}
                src={upLoadState.previewImage}
              />
            )}
          </div>
        );
      // 文件上传至s3的组件
      case 'remoteUpload':
        return (
          <RemoteUploader
            {...remoteUploadProps}
            fieldName={String(fieldName)}
            type={type}
            max={max}
            showUploadList={showUploadList ?? true}
            actionUrl={actionUrl || ''}
          />
        );
      default:
        return null;
    }
  };

  const onFinish = async (values: any) => {
    const formValue = await form.validateFields();
    onSearch && onSearch(formValue);
  };

  /**
   * Formats the given form data by modifying certain properties.
   * @param {FormConfig}formData - The form configuration data.
   * @return {FieldItem}The formatted form data.
   */
  const formatFormData = (formData: FormConfig) => {
    return {
      ...formData,
      data: formData?.data?.map((item: FieldItem) => {
        if (item.hidden) {
          return {
            ...item,
            validatorRules: [{ required: false }],
          };
        }
        return item;
      }),
    };
  };

  /**
   * This function is triggered when there are changes in the fields.
   * It updates the visibility and validation rules based on the changed values.
   *
   * @param {Object} changedValues - The changed values of the fields.
   * @param {Object} allValues - All the values of the fields.
   */
  const onFieldsChange = (changedValues: any, allValues: any) => {
    const changedValue = changedValues[0];
    const changedName = changedValue?.name[0];
    onFieldChange && onFieldChange(changedName, changedValue);
    // Iterate through the link rules in the form data
    for (const i of formData?.linkRules || []) {
      if (i.fieldName === changedName) {
        const { linkFieldName, type, visibleRule } = i;

        if (type.includes('visible') && visibleRule) {
          const { show } = visibleRule;

          // Find the link field in the form data
          const linkField = formData.data.find(
            (field: FieldItem) => field.fieldName === linkFieldName
          );

          // Update the visibility of the link field
          linkField.hidden = !show.includes(changedValue.value);
          // Update the required validation rule of the link field
          customValidatorRef.current.forEach((item: any) => {
            if (linkField.fieldName === item.fieldName && !linkField.hidden) {
              linkField.validatorRules = cloneDeep(item.validatorRules);
            } else if (
              linkField.fieldName === item.fieldName &&
              linkField.hidden
            ) {
              if (linkField?.validatorRules) {
                linkField.validatorRules = [{ required: false }];
              }
            }
          });
        }
      }
    }
    // Update the form data
    setFormData({ ...formData });
  };

  useEffect(() => {
    getFormInstance && getFormInstance(form);
  }, []);

  useEffect(() => {
    if (enableSelectAllField) {
      const field = formData?.data?.find(
        (item: any) => item.fieldName === enableSelectAllField
      );
      const valueArr = form.getFieldValue(enableSelectAllField);
      const fieldLength = formatCommonCategory(field?.data).length;
      if (valueArr && valueArr.length > 0 && valueArr.length < fieldLength) {
        setAllSelected(false);
      } else if (valueArr && valueArr.length === fieldLength) {
        setAllSelected(true);
      } else if (valueArr && valueArr.length === 0) {
        setAllSelected(false);
      }
    }
  }, [enableSelectAllField && form.getFieldValue(enableSelectAllField)]);
  return (
    <div className="form-view">
      <Form
        name="complex-form"
        labelAlign={labelAlign || 'right'}
        layout={layout}
        size="large"
        form={form}
        initialValues={defaultValue || {}}
        autoComplete="off"
        onFieldsChange={onFieldsChange}
        onValuesChange={onValuesChange}
        className={className || ''}
        style={{
          alignItems: 'center',
        }}
      >
        {formData?.data?.map((item: FieldItem) => {
          return (
            <Form.Item
              labelCol={item.labelCol || { span: 6 }}
              wrapperCol={item.wrapperCol}
              key={item.fieldName.toString()}
              name={item.fieldName}
              label={item.label}
              help={item.expandNode}
              rules={item.validatorRules ?? []}
              hidden={!!item.hidden}
              className={'field_' + item.fieldName.toString()}
            >
              {renderFieldItem(item)}
            </Form.Item>
          );
        })}
        {onSearch && (
          <Button className="search-btn" type="primary" onClick={onFinish}>
            查询
          </Button>
        )}
        {onReset && (
          <Button
            htmlType="button"
            onClick={() => {
              form.resetFields();
              onReset();
            }}
          >
            重置
          </Button>
        )}
      </Form>
    </div>
  );
};

export default React.memo(CommonForm);
